import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowsLeftRight,
  Heart,
  PaperPlaneTilt,
  Trash,
  X,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { clearWishlist, removeWishlist } from "@/lib/wishlist";

// Saved slabs from any product page: send the whole list to the shop, or pick 2–3 to compare
export const WishlistPanel = ({ list, onClose, onSend }) => {
  const navigate = useNavigate();
  const [picked, setPicked] = useState([]);
  const stones = [...new Set(list.map((w) => w.key))];

  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const send = () => {
    const lines = list.map(
      (w) => `• ${w.color} — item ${w.item_no || "—"} (${w.size})`,
    );
    onSend(
      `I'm interested in these slabs:\n${lines.join("\n")}\n\nPlease let me know availability and pricing.`,
    );
  };
  const toggle = (key) =>
    setPicked((p) =>
      p.includes(key)
        ? p.filter((k) => k !== key)
        : p.length < 3
          ? [...p, key]
          : p,
    );
  const compare = () => {
    onClose();
    navigate(`/compare?keys=${picked.join(",")}`);
  };

  return (
    <div
      className="site site-overlay fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto p-4 sm:p-10"
      data-testid="wishlist-panel"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Your wishlist"
    >
      <div
        className="relative w-full max-w-2xl  border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-6 sm:p-10"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <button
          type="button"
          data-testid="wishlist-close"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center  border border-[color:var(--s-line)] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]"
        >
          <X size={16} weight="bold" />
        </button>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center  bg-[#2e2c29] text-white">
            <Heart size={20} weight="fill" />
          </span>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">
              Your wishlist
            </h2>
            <p className="text-sm text-[color:var(--s-mute)]">
              {list.length
                ? `${list.length} slab${list.length === 1 ? "" : "s"} saved on this device${
                    stones.length >= 2 ? " · tick 2–3 to compare" : ""
                  }`
                : "Tap Wishlist on any slab to keep it here"}
            </p>
          </div>
        </div>

        {list.length > 0 && (
          <div className="mt-6 grid gap-2" data-testid="wishlist-items">
            {list.map((w) => {
              const on = picked.includes(w.key);
              return (
                <div
                  key={w.id}
                  data-testid={`wishlist-item-${w.id}`}
                  className={`s-chip !flex-row !justify-start gap-4 !px-3 !py-3 text-left ${
                    on ? "ring-2 ring-[color:var(--s-gold)]" : ""
                  }`}
                >
                  {stones.length >= 2 && (
                    <input
                      type="checkbox"
                      data-testid={`wishlist-pick-${w.id}`}
                      checked={on}
                      onChange={() => toggle(w.key)}
                      disabled={!on && picked.length >= 3}
                      aria-label={`Compare ${w.color}`}
                      className="h-5 w-5 shrink-0 accent-[#c39e6d]"
                    />
                  )}
                  <Link
                    to={`/inventory/${w.key}`}
                    onClick={onClose}
                    className="s-fig h-14 w-20 shrink-0 overflow-hidden  bg-[#e6e1d8]"
                  >
                    {w.thumb ? (
                      <img
                        src={`${API}/public/showroom/photo/${w.thumb}?w=400`}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/inventory/${w.key}`}
                      onClick={onClose}
                      className="block truncate text-base font-semibold tracking-tight hover:underline"
                    >
                      {w.color}
                    </Link>
                    <div className="text-xs text-[color:var(--s-mute)]">
                      Item {w.item_no || "—"} · {w.size}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`wishlist-remove-${w.id}`}
                    onClick={() => removeWishlist(w.id)}
                    aria-label="Remove from wishlist"
                    className="s-icon-btn !h-11 !w-11 shrink-0 !bg-white"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {list.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="wishlist-send"
              onClick={send}
              className="s-btn"
            >
              <PaperPlaneTilt size={14} weight="bold" /> Send my wishlist
            </button>
            {stones.length >= 2 && (
              <button
                type="button"
                data-testid="wishlist-compare"
                onClick={compare}
                disabled={picked.length < 2}
                className="s-ghost disabled:opacity-40"
              >
                <ArrowsLeftRight size={14} weight="bold" /> Compare
                {picked.length ? ` (${picked.length})` : ""}
              </button>
            )}
            <button
              type="button"
              data-testid="wishlist-clear"
              onClick={clearWishlist}
              className="s-ghost"
            >
              Clear list
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPanel;
